import { useMemo } from 'react'
import { useHasAccess, useAuth } from './auth'
import { useEvent } from './events'
import { useGetProducerPDV } from './producer-pdv'
import {
  SECONDARY_BLUE_01,
  SECONDARY_GREEN_07,
  PRIMARY_RED_01,
  SECONDARY_ORANGE_01,
  SECONDARY_YELLOW_05,
} from 'bd-web-components/src/utils/dash-colors'

export interface NavLinkItem {
  key: string
  label: string
  pattern: string
  /** Rota que casa com `pattern` mas é de outro link (ver utils/router isNavLinkActive). */
  exclude?: string
  to: string
  visible: boolean
}

export interface EventMenuChildItem {
  path: string
  activePaths?: string[]
  title: string
  // optional metadata for native drawer creation
  id?: string
  drawer?: boolean
  drawerIcon?: string
}

export interface EventMenuItem {
  path: string
  title: string
  icon: string
  collapse: boolean
  activePaths?: string[]
  children?: EventMenuChildItem[]
  // optional metadata for native drawer creation at top-level
  id?: string
  drawer?: boolean
  drawerIcon?: string
}

export type EventActionCategory =
  | 'financeiro'
  | 'relatorios'
  | 'marketing'
  | 'ferramentas'

export interface EventActionItem {
  key: string
  title: string
  description: string
  to: string
  iconName: string
  iconFill: string
  category: EventActionCategory
  badge?: string
}

export const useEventAccess = (id?: string) => {
  const { event } = useEvent(Number(id))
  const { user } = useAuth()
  const { producerPDV } = useGetProducerPDV(id as string)

  const hasTempAccess = useHasAccess('DPTEMP')
  const accessDashboard = useHasAccess('DP2')
  const accessRequestWithdraw =
    useHasAccess('DP3') &&
    (event?.clienteId === user?.clienteId || user?.clienteId == null)
  const accessWithdraw = useHasAccess('DP4')
  const accessFinancial = useHasAccess('DP7')
  const accessPixel = useHasAccess('DP8')
  const accessAbandonedCart = useHasAccess('DP9')
  const newAccessSummaryPage = useHasAccess('DP14')
  const newSalesSummaryPage = useHasAccess('DP15')
  const accessSpotify = useHasAccess('DP10')
  const accessOS = useHasAccess('OS1')
  const accessReceiptsCalendar = useHasAccess('DP17')
  const accessValidador = useHasAccess('DP18')
  const accessReviews = useHasAccess('DP22')

  return {
    hasTempAccess,
    accessDashboard,
    accessRequestWithdraw,
    accessWithdraw,
    accessFinancial,
    accessPixel,
    accessAbandonedCart,
    producerPDV,
    accessSpotify,
    newAccessSummaryPage,
    newSalesSummaryPage,
    accessOS,
    accessReceiptsCalendar,
    accessValidador,
    accessReviews,
  }
}

export const useHeaderLinks = (id?: string): NavLinkItem[] => {
  const accessAddAccount = useHasAccess('DP5')
  const accessEstoque = useHasAccess('ESL1') // OP-3605
  const accessSolicitar = useHasAccess('ESL2') // OP-3605: solicitante entra por 'Minhas solicitações'
  const accessCourtesy = useHasAccess('DP11')
  const accessComparison = useHasAccess('DP19')
  const accessBordero = useHasAccess('DP20')
  const accessOS = useHasAccess('OS1')

  return useMemo<NavLinkItem[]>(
    () => [
      {
        key: 'start',
        label: 'Início',
        pattern: 'start/*',
        to: '/start',
        visible: true,
      },
      {
        key: 'events',
        label: 'Eventos',
        pattern: 'events/*',
        to: '/events',
        visible: true,
      },
      {
        key: 'analytics',
        label: 'Analytics',
        pattern: 'analytics/comparativo',
        to: '/analytics/comparativo',
        visible: accessComparison,
      },
      {
        key: 'bordero',
        label: 'Borderô',
        pattern: 'analytics/bordero',
        to: '/analytics/bordero',
        visible: accessBordero,
      },
      {
        key: 'finance',
        label: 'Financeiro',
        pattern: 'finance/*',
        to: '/finance',
        visible: accessAddAccount,
      },
      {
        key: 'courtesy',
        label: 'Cortesias',
        pattern: 'courtesy/*',
        to: '/courtesy/events',
        visible: accessCourtesy,
      },
      {
        key: 'os',
        label: 'Ordem de Serviço',
        pattern: 'interno/*',
        exclude: 'interno/estoque/*', // OP-3605: /interno/estoque é do link de Estoque
        to: '/interno/os',
        visible: accessOS,
      },
      {
        key: 'estoque',
        label: 'Estoque',
        pattern: 'interno/estoque/*',
        to: accessEstoque ? '/interno/estoque/posicao' : '/interno/estoque/minhas',
        visible: accessEstoque || accessSolicitar,
      },
    ],
    [accessAddAccount, accessCourtesy, accessComparison, accessBordero, accessOS, accessEstoque, accessSolicitar]
  )
}

export const useEventMenu = (id?: string): EventMenuItem[] => {
  const {
    accessDashboard,
    accessRequestWithdraw,
    accessWithdraw,
    accessFinancial,
    accessAbandonedCart,
    accessSpotify,
    accessReviews,
    producerPDV,
    accessReceiptsCalendar,
  } = useEventAccess(id)

  return useMemo<EventMenuItem[]>(() => {
    if (!id) return []

    const data: EventMenuItem[] = []

    const eventData: EventMenuItem = {
      path: `/events/${id}`,
      title: 'Evento',
      icon: 'multimediaTicket',
      collapse: false,
      activePaths: [
        `/events/${id}`,
        `/events/${id}/abandoned-cart`,
        `/events/${id}/spotify`,
        `/events/${id}/reviews`,
      ],
      children: [
        {
          path: `/events/${id}`,
          activePaths: [`/events/${id}`],
          title: 'Detalhes do Evento',
        },
      ],
    }

    if (accessAbandonedCart) {
      eventData.collapse = true
      eventData.children?.push(
        {
          path: `/events/${id}/abandoned-cart`,
          activePaths: [`/events/${id}/abandoned-cart`],
          title: 'Carrinho abandonado',
        },
      )
    }

    if (accessReviews) {
      eventData.collapse = true
      eventData.children?.push({
        path: `/events/${id}/reviews`,
        activePaths: [`/events/${id}/reviews`],
        title: 'Avaliações',
        id: 'reviews',
        drawer: true,
        drawerIcon: 'star',
      })
    }

    if (accessSpotify) {
      eventData.collapse = true
      eventData.children?.push({
        path: `/events/${id}/spotify`,
        activePaths: [`/events/${id}/spotify`],
        title: 'Spotify',
      })
    }

    data.push(eventData)

    if (accessDashboard || accessFinancial) {
      const reports: EventMenuItem = {
        path: '#',
        title: 'Relatórios',
        icon: 'businessListFormat',
        collapse: true,
        children: [],
      }

      if (accessDashboard) {
        reports.children!.push({
          path: `/events/${id}/dashboard`,
          activePaths: [`/events/${id}/dashboard`],
          title: 'Dashboard de Vendas',
        })
      }

      if (accessFinancial) {
        reports.children!.push({
          path: `/events/${id}/financial`,
          activePaths: [`/events/${id}/financial`],
          title: 'Fechamento Finaceiro',
        })
      }

      if (reports.children && reports.children.length > 0) {
        data.push(reports)
      }
    }

    if (accessRequestWithdraw || accessWithdraw) {
      const withdraw: EventMenuItem = {
        path: '#',
        title: 'Saque',
        icon: 'businessMoneyWithdrawal',
        collapse: true,
        children: [],
      }

      if (accessRequestWithdraw) {
        withdraw.children!.push({
          path: `/events/${id}/withdraw/requests/list`,
          activePaths: [
            `/events/${id}/withdraw/requests/list`,
            `/events/${id}/withdraw/requests/new`,
          ],
          title: 'Solicitar Saque',
          id: 'withdraw-requests',
          drawer: true,
          drawerIcon: 'dollar-sign',
        })
      }

      if (accessReceiptsCalendar) {
        withdraw.children!.push({
          path: `/events/${id}/withdraw/requests/calendar`,
          activePaths: [`/events/${id}/withdraw/requests/calendar`],
          title: 'Calendário de Recebimentos',
          id: 'receipts-calendar',
          drawer: true,
          drawerIcon: 'calendar',
        })
      }

      if (withdraw.children && withdraw.children.length > 0) {
        data.push(withdraw)
      }
    }

    if (producerPDV !== undefined) {
      data.push({
        path: '#',
        title: 'Cortesias',
        icon: 'tag',
        collapse: true,
        children: [
          {
            path: `/events/${id}/courtesy`,
            activePaths: [`/events/${id}/courtesy`],
            title: 'Envio de cortesias',
            id: 'courtesy',
            drawer: true,
            drawerIcon: 'tag',
          },
          {
            path: `/events/${id}/courtesy/list`,
            activePaths: [`/events/${id}/courtesy/list`],
            title: 'Cortesias enviadas',
            id: 'courtesy-list',
            drawer: true,
            drawerIcon: 'tag',
          },
        ],
      })
    }

    return data
  }, [
    id,
    accessAbandonedCart,
    accessDashboard,
    accessFinancial,
    accessRequestWithdraw,
    accessWithdraw,
    producerPDV,
    accessSpotify,
    accessReviews,
    accessReceiptsCalendar,
  ])
}

export const useEventActions = (id?: string): EventActionItem[] => {
  const {
    hasTempAccess,
    accessDashboard,
    accessRequestWithdraw,
    accessFinancial,
    accessPixel,
    accessAbandonedCart,
    producerPDV,
    accessSpotify,
    accessReviews,
    newAccessSummaryPage,
    newSalesSummaryPage,
    accessOS,
    accessReceiptsCalendar,
    accessValidador,
  } = useEventAccess(id)

  return useMemo<EventActionItem[]>(() => {
    if (!id) return []
    const actions: EventActionItem[] = []

    // Financeiro -------------------------------------------------
    if (accessFinancial) {
      actions.push({
        key: 'financial',
        title: 'Fechamento do evento',
        description: 'Veja o resultado final do seu evento',
        to: `/events/${id}/financial`,
        iconName: 'businessBill',
        iconFill: PRIMARY_RED_01,
        category: 'financeiro',
      })
    }

    if (accessRequestWithdraw) {
      actions.push({
        key: 'withdraw',
        title: 'Solicitar saque',
        description: 'Solicite o repasse das suas vendas',
        to: `/events/${id}/withdraw`,
        iconName: 'businessIncreaseMoney',
        iconFill: SECONDARY_GREEN_07,
        category: 'financeiro',
      })
    }

    if (accessReceiptsCalendar) {
      actions.push({
        key: 'receipts-calendar',
        title: 'Calendário de recebimentos',
        description: 'Acompanhe as datas de repasse',
        to: `/events/${id}/withdraw/requests/calendar`,
        iconName: 'businessCalendar',
        iconFill: SECONDARY_GREEN_07,
        category: 'financeiro',
      })
    }

    if (accessOS) {
      actions.push({
        key: 'os',
        title: 'Ordem de serviço',
        description:
          'Custos operacionais: técnicos, equipamentos e deslocamento',
        to: `/interno/os/evento/${id}`,
        iconName: 'businessBill',
        iconFill: SECONDARY_ORANGE_01,
        category: 'financeiro',
      })
    }

    // Relatórios -------------------------------------------------
    if (accessDashboard) {
      actions.push({
        key: 'dashboard',
        title: 'Dashboard',
        description: 'Métricas de performance em tempo real',
        to: `/events/${id}/dashboard`,
        iconName: 'solidChart',
        iconFill: SECONDARY_BLUE_01,
        category: 'relatorios',
      })
    }

    if (newSalesSummaryPage) {
      actions.push({
        key: 'sales-sumary',
        title: 'Relatório de vendas',
        description: 'Acompanhe as vendas do seu evento',
        to: `/events/${id}/sales-sumary`,
        iconName: 'businessBill',
        iconFill: SECONDARY_BLUE_01,
        category: 'relatorios',
      })
    }

    if (newAccessSummaryPage) {
      actions.push({
        key: 'access-sumary',
        title: 'Relatório de acesso',
        description: 'Monitore o acesso e a validação no local',
        to: `/events/${id}/access-sumary`,
        iconName: 'businessBill',
        iconFill: SECONDARY_BLUE_01,
        category: 'relatorios',
      })
    }

    if (accessReviews) {
      actions.push({
        key: 'reviews',
        title: 'Avaliações',
        description: 'Veja as avaliações enviadas pelos clientes do evento',
        to: `/events/${id}/reviews`,
        iconName: 'report',
        iconFill: SECONDARY_YELLOW_05,
        category: 'relatorios',
      })
    }

    // Marketing --------------------------------------------------
    if (accessAbandonedCart) {
      actions.push({
        key: 'abandoned-cart',
        title: 'Carrinho abandonado',
        description: 'Recupere vendas que não foram concluídas',
        to: `/events/${id}/abandoned-cart`,
        iconName: 'shoppingCart',
        iconFill: SECONDARY_GREEN_07,
        category: 'marketing',
      })
    }

    if (accessPixel) {
      actions.push({
        key: 'pixel',
        title: 'Tags & Pixels',
        description: 'Monitore o comportamento dos consumidores',
        to: `/events/${id}/pixel`,
        iconName: 'tag',
        iconFill: SECONDARY_ORANGE_01,
        category: 'marketing',
      })
    }

    // Ferramentas ------------------------------------------------
    if (accessValidador) {
      actions.push({
        key: 'validador',
        title: 'Validador de ingressos',
        description: 'Valide ingressos pela câmera ou por busca',
        to: `/events/${id}/validador`,
        iconName: 'multimediaTicket',
        iconFill: SECONDARY_BLUE_01,
        category: 'ferramentas',
        badge: 'novo',
      })
    }

    if (producerPDV !== undefined && hasTempAccess) {
      actions.push({
        key: 'courtesy',
        title: 'Cortesias',
        description:
          'Gerencie e envie ingressos exclusivos do seu evento',
        to: `/events/${id}/courtesy`,
        iconName: 'tag',
        iconFill: SECONDARY_YELLOW_05,
        category: 'ferramentas',
      })
    }

    if (accessSpotify) {
      actions.push({
        key: 'spotify',
        title: 'Spotify',
        description: 'Gerencie as configurações do Spotify para este evento',
        to: `/events/${id}/spotify`,
        iconName: 'spotify',
        iconFill: SECONDARY_BLUE_01,
        category: 'ferramentas',
      })
    }

    return actions
  }, [
    id,
    accessDashboard,
    accessRequestWithdraw,
    accessFinancial,
    accessPixel,
    accessAbandonedCart,
    hasTempAccess,
    producerPDV,
    accessSpotify,
    accessReviews,
    newAccessSummaryPage,
    newSalesSummaryPage,
    accessOS,
    accessReceiptsCalendar,
    accessValidador,
  ])
}
